/* eslint-disable max-lines -- the parent now owns the SidebarProps surface
 * and the cross-hook glue (workspace-tree slices, channel-actions ctx,
 * dialog state, command registrations, JSX prop wiring for SidebarDialogs +
 * SidebarHomeContent + SidebarWorkspaceContent). All discrete state
 * machines + pure logic have been extracted into sibling files under
 * `sidebar/`; what remains is irreducible plumbing. Mirrors the structural
 * choice in InboxView.tsx after its P5 split. */
import {
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { queries } from "@/lib/queries";
import {
  useUserList,
  useActiveUsers,
  useInboxBadgeCount,
  useUnreadNotifications,
} from "@/lib/selectors";
// D.1 composite — one listener replaces 5 of the selector-hook
// subscriptions above (myMemberships, mutedSet, unreadCount, mentionCount,
// lastMessageAt) + the 3 channel useQuery calls below. Identity-stable
// Maps per slice; consumers keep reading `membershipMap.get(id)`,
// `unreadCountMap.get(id)`, etc.
import { useSidebarTree } from "@/lib/selectors/sidebar-tree";
import { Tray, ChatText, EnvelopeSimple } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { Separator } from "@/components/ui/separator";
import { RENDER_DEBUG } from "@/lib/debug-flags";
import { csNow, csLog } from "@/lib/channel-switch-profile";
import { useCurrentUser } from "@/lib/current-user";
import { usePreloadChannels } from "@/hooks/use-preload-channels";
import { useExistingDmUserIds } from "@/hooks/use-existing-dm-user-ids";
import { useAuth } from "@workos-inc/authkit-react";
import { NavItem } from "./NavItem";
import { UserProfileFooter } from "./UserProfileFooter";
import { QuickInvite } from "@/components/dm/QuickInvite";
import { TopSearchBar } from "./TopSearchBar";
import { SidebarUpdateBanner } from "./SidebarUpdateBanner";
import { useRegisterCommand } from "@/lib/commands/CommandProvider";
import {
  findAdjacentChannelId,
  readSidebarChannelOrder,
} from "./sidebar/channel-navigation";
import { useSidebarMembershipLossDetection } from "@/hooks/useSidebarMembershipLossDetection";
import { useSidebarUIModalStates } from "@/hooks/useSidebarUIModalStates";
import { useSidebarWorkspaceSelectionWithFallback } from "@/hooks/useSidebarWorkspaceSelectionWithFallback";
import { useSidebarSettingsOpenListener } from "@/hooks/useSidebarSettingsOpenListener";
import { WorkspaceSwitcher } from "./sidebar/workspace-switcher";
import { SidebarHomeContent } from "./sidebar/home-content";
import { SidebarDialogs } from "./sidebar/sidebar-dialogs";
import { useSidebarPreferences } from "./sidebar/use-sidebar-preferences";
import { useSidebarHomeData } from "./sidebar/use-sidebar-home-data";
import { useSidebarWorkspaceData } from "./sidebar/use-sidebar-workspace-data";
import { useChannelActions } from "./sidebar/use-channel-actions";
import { SidebarWorkspaceContent } from "./sidebar/workspace-content";
import { WorkspaceSettingsMenu } from "./sidebar/workspace-settings-menu";
import { cn } from "@/lib/utils";

type CurrentUserRow = {
  id: string;
  display_name?: string | null;
  avatar_url?: string | null;
  status?: string | null;
  status_emoji?: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type WorkspaceRow = any;

interface SidebarProps {
  scope: string;
  onScopeChange: (scope: string) => void;
  onShowInbox: () => void;
  showInbox: boolean;
  selectedWorkspace: string | null;
  onSelectWorkspace: (id: string | null) => void;
  selectedChannel: string | null;
  onSelectChannel: (id: string | null, name?: string) => void;
  openChannelWithContext?: (
    channelId: string,
    opts?: { workspaceId?: string | null; targetMessageId?: string },
  ) => void;
  showLists?: boolean;
  onToggleLists?: () => void;
  showCoworkers?: boolean;
  onToggleCoworkers?: () => void;
  showAutomations?: boolean;
  onToggleAutomations?: () => void;
  showInvites?: boolean;
  onToggleInvites?: () => void;
  selectedListId?: string | null;
  onSelectList?: (id: string) => void;
  onCreateListItem?: (preselectedListId?: string) => void;
  onGoHome?: () => void;
  onMembershipLost?: (workspaceName: string) => void;
  workspaceList?: WorkspaceRow[];
  isGuest?: boolean;
  onSignUp?: () => void;
  // Called by the create-workspace flow BEFORE selecting the new workspace
  // so the scope auto-recovery effect doesn't bounce to "home" while the
  // CP-list refetch is in flight. See use-workspace-scope.ts auto-recovery.
  setPendingWorkspace?: (id: string | null) => void;
  // Mobile-only: when the Sidebar is rendered inside a Sheet drawer, drop
  // decorations that only belong in the desktop AppShell flex layout
  // (outer rounded corner, right-edge divider, Electron titlebar drag
  // region) and stretch to the drawer height so the inner scroll area
  // gets a height constraint.
  inDrawer?: boolean;
}

export const Sidebar = memo(function Sidebar({
  scope,
  onScopeChange,
  onShowInbox,
  showInbox,
  selectedWorkspace,
  onSelectWorkspace,
  selectedChannel,
  onSelectChannel,
  openChannelWithContext: openChannelWithContextProp,
  showLists,
  onToggleLists,
  showCoworkers,
  onToggleCoworkers,
  showAutomations,
  onToggleAutomations,
  showInvites,
  onToggleInvites,
  selectedListId,
  onSelectList,
  onCreateListItem,
  onGoHome,
  onMembershipLost,
  workspaceList: workspaceListProp,
  isGuest,
  onSignUp,
  setPendingWorkspace,
  inDrawer = false,
}: SidebarProps) {
  // ─── Render diagnostics ─────────────────────────────────────────
  const sidebarRenderCountRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  sidebarRenderCountRef.current += 1;

  // Channel-switch profile — measure the synchronous render body cost
  // and (via the useLayoutEffect below) the total commit cost
  // including all child renders. Difference between the two attributes
  // longtask cost to "this component's body" vs "descendant subtree".
  const csRenderStart = csNow();
  const csRenderStartRef = useRef(0);
  // eslint-disable-next-line react-hooks/refs
  csRenderStartRef.current = csRenderStart;

  const { user } = useCurrentUser();
  const { getAccessToken } = useAuth();
  const zero = useZero();
  const [userList] = useUserList();
  const [activeUserList] = useActiveUsers();
  const [tree] = useSidebarTree();
  const {
    channelMap,
    membershipMap,
    mutedSet,
    unreadMap: unreadCountMap,
    mentionMap: mentionCountMap,
    lastMessageMap: lastMessageAtMap,
  } = tree;
  const myMembers = useMemo(
    () => Array.from(membershipMap.values()),
    [membershipMap],
  );
  const [inboxBadgeCount] = useInboxBadgeCount();
  const [unreadNotifications] = useUnreadNotifications();

  if (RENDER_DEBUG)
    console.debug(
      // eslint-disable-next-line react-hooks/refs
      `[Sidebar] render #${sidebarRenderCountRef.current}`,
      `scope=${scope} channels=${myMembers.length} selected=${selectedChannel?.slice(-6) ?? "none"}`,
    );

  // Scoped inbox badge: filter by current scope
  const scopedInboxBadgeCount = useMemo(() => {
    if (scope === "home") return inboxBadgeCount; // Home shows all
    return unreadNotifications.filter(
      (n: { workspace_id?: string | null }) => n.workspace_id === scope,
    ).length;
  }, [scope, inboxBadgeCount, unreadNotifications]);

  const currentUserData = useMemo(
    () =>
      user ? userList.find((u: CurrentUserRow) => u.id === user.id) : null,
    [user, userList],
  );
  const [showNewDM, setShowNewDM] = useState(false);

  // Use workspaceList from App (avoid duplicate query)
  const [workspaceListFallbackRaw] = useQuery(
    !workspaceListProp && user ? queries.workspaces.byUser() : null,
  );
  const workspaceList = useMemo(
    () => workspaceListProp ?? workspaceListFallbackRaw ?? [],
    [workspaceListProp, workspaceListFallbackRaw],
  );

  const isHomeScope = scope === "home";
  const isWorkspaceScope = !isHomeScope;

  // ─── Home scope data ───────────────────────────────────────────
  const {
    homeWsOpen,
    setHomeWsOpen,
    homeDmsOpen,
    setHomeDmsOpen,
    homeWsFilter,
    setHomeWsFilter,
    homeDmsFilter,
    setHomeDmsFilter,
    showCreateWorkspaceHome,
    setShowCreateWorkspaceHome,
    workspaceUnreads,
    homeDisplayWorkspaces,
    bestLookupWorkspaceId,
  } = useSidebarHomeData({
    isHomeScope,
    channelMap,
    mutedSet,
    unreadCountMap,
    mentionCountMap,
    lastMessageAtMap,
    workspaceList,
  });

  // ─── Workspace scope data ─────────────────────────────────────
  const {
    workspaceLists,
    channelById,
    archivedChannels,
    myWsMembershipsStatus,
    homeMembershipMap,
    collaboratorIds,
    wsMemberIdSet,
    channelsWithCollaborators,
    clientSpaces,
    channelList,
    currentUserWsMembership,
    isWorkspaceAdmin,
    isCollaborator,
    canManageChannel,
    canDeleteChannel,
    spaceChannelMap,
  } = useSidebarWorkspaceData({
    selectedWorkspace,
    user,
    channelMap,
    membershipMap,
    myMembers,
  });

  // Existing DM user IDs for relationship badges in NewDM dialog
  const existingDmUserIds = useExistingDmUserIds(user?.id, showNewDM);

  const currentWorkspace = workspaceList.find(
    (w) => w.id === selectedWorkspace,
  );

  // Regular (non-admin) members of a PUBLIC workspace don't get the Team
  // (member roster) page — keep the full membership out of drive-by
  // joiners' reach. Admins keep access; private workspaces are unchanged.
  const canAccessTeam = !currentWorkspace?.is_public || isWorkspaceAdmin;

  const isUnread = useCallback(
    (chId: string) => {
      if (chId === selectedChannel) return false;
      if (mutedSet.has(chId)) return false;
      return (unreadCountMap.get(chId) || 0) > 0;
    },
    [selectedChannel, mutedSet, unreadCountMap],
  );

  const getMentionCount = (chId: string) => {
    if (chId === selectedChannel) return 0;
    if (mutedSet.has(chId)) return 0;
    return mentionCountMap.get(chId) || 0;
  };

  // Workspace-wide message preload — populates the local Zero cache
  // with recent messages + sender for every accessible channel in
  // the active workspace plus all DMs. ONE subscription instead of
  // 15 per-channel preloads. See `queries.channels.byWorkspaceWith
  // RecentMessages` for the shape and PR #1326/#1345 follow-up plan.
  usePreloadChannels(selectedWorkspace);

  // Dialog state
  const {
    showCreateWorkspace,
    setShowCreateWorkspace,
    createChannelContext,
    setCreateChannelContext,
    showEditChannel,
    setShowEditChannel,
    showDeleteChannel,
    setShowDeleteChannel,
    showBrowseChannels,
    setShowBrowseChannels,
    showWorkspaceSettings,
    setShowWorkspaceSettings,
    showCreateClientSpace,
    setShowCreateClientSpace,
    selectedClientSpace,
    setSelectedClientSpace,
    inviteContext,
    setInviteContext,
    contextChannel,
    setContextChannel,
  } = useSidebarUIModalStates();

  // Persisted UI preferences (per-section open/filter + expanded spaces)
  const {
    channelsOpen,
    setChannelsOpen,
    channelsFilter,
    setChannelsFilter,
    spacesOpen,
    setSpacesOpen,
    spacesFilter,
    setSpacesFilter,
    dmsOpen,
    setDmsOpen,
    dmsFilter,
    setDmsFilter,
    listsOpen,
    setListsOpen,
    expandedSpaces,
    setExpandedSpaces,
  } = useSidebarPreferences({ selectedWorkspace, userId: user?.id });

  const toggleSpaceExpanded = (spaceId: string) => {
    setExpandedSpaces((prev) => ({ ...prev, [spaceId]: !prev[spaceId] }));
  };

  const displayChannels = useMemo(() => {
    if (channelsFilter === "unread")
      return channelList.filter((ch) => isUnread(ch.id));
    if (channelsFilter === "recent") {
      const joinedAtMap = new Map<string, number>();
      for (const m of myMembers) {
        joinedAtMap.set(m.channel_id, m.joined_at);
      }
      return [...channelList]
        .filter((ch) => lastMessageAtMap.get(ch.id) || joinedAtMap.get(ch.id))
        .sort(
          (a, b) =>
            (lastMessageAtMap.get(b.id) ?? joinedAtMap.get(b.id) ?? 0) -
            (lastMessageAtMap.get(a.id) ?? joinedAtMap.get(a.id) ?? 0),
        )
        .slice(0, 5);
    }
    return channelList;
  }, [channelList, channelsFilter, lastMessageAtMap, isUnread, myMembers]);

  // Client spaces don't have per-space channel data for unread/recent yet,
  // so only "all" shows the full list; other filters are no-ops for now.
  const displaySpaces = useMemo(() => clientSpaces, [clientSpaces]);

  const settingsMenuTriggerRef = useRef<HTMLButtonElement>(null);
  const [headerPortalEl, setHeaderPortalEl] = useState<HTMLDivElement | null>(
    null,
  );

  // Listen for programmatic settings open (e.g. from terminal integration cards)
  useSidebarSettingsOpenListener({
    onOpenSettings: () => setShowWorkspaceSettings(true),
  });

  useRegisterCommand("sidebar.toggleNewMenu", () => {
    settingsMenuTriggerRef.current?.click();
  });
  useRegisterCommand("sidebar.newDirectMessage", () => setShowNewDM(true));
  useRegisterCommand("sidebar.browseChannels", () =>
    setShowBrowseChannels(true),
  );
  useRegisterCommand("sidebar.newChannel", () =>
    setCreateChannelContext("workspace"),
  );
  useRegisterCommand("sidebar.newWorkspace", () =>
    setShowCreateWorkspace(true),
  );
  useRegisterCommand("app.openSettings", () => setShowWorkspaceSettings(true));

  // Alt+↑ / Alt+↓ switch to the prev/next channel in the visible
  // sidebar order (DOM order — sections/filters/expand state are all
  // respected because anything collapsed is unmounted, not just
  // hidden). No wrap at the top/bottom.
  //
  // Leading-edge + trailing-edge debounce: a single press fires
  // immediately (snappy), but rapid presses within
  // `NAVIGATE_BURST_MS` only walk a pending target instead of
  // kicking off a full channel-switch pipeline per press. Once the
  // burst ends, the final pending target commits — so holding
  // Alt+↓ across 8 channels does 1 commit at the start + 1 at the
  // end, not 8 sequential remounts queued behind each other.
  const NAVIGATE_BURST_MS = 70;
  const lastFiredAtRef = useRef(0);
  const pendingNavTargetRef = useRef<string | null>(null);
  const navBurstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedChannelRef = useRef(selectedChannel);
  // eslint-disable-next-line react-hooks/refs
  selectedChannelRef.current = selectedChannel;
  const fireOpenChannel = useCallback(
    (id: string) => {
      lastFiredAtRef.current =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (openChannelWithContextProp) {
        openChannelWithContextProp(id);
      } else {
        onSelectChannel(id);
      }
    },
    [openChannelWithContextProp, onSelectChannel],
  );
  const navigateChannel = useCallback(
    (direction: -1 | 1) => {
      const ids = readSidebarChannelOrder(document);
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      const insideBurst = now - lastFiredAtRef.current < NAVIGATE_BURST_MS;
      // Continue walking from the pending target while inside a
      // burst so successive presses traverse the list as expected;
      // start from the actual selection on a fresh press.
      const startFrom =
        insideBurst && pendingNavTargetRef.current !== null
          ? pendingNavTargetRef.current
          : selectedChannel;
      const nextId = findAdjacentChannelId(ids, startFrom, direction);
      if (!nextId) return;
      pendingNavTargetRef.current = nextId;

      if (!insideBurst) {
        // Leading-edge: single press fires immediately.
        fireOpenChannel(nextId);
      }

      // Trailing-edge: (re)schedule the final commit. If the user
      // keeps pressing, this timer keeps getting reset; once they
      // pause for NAVIGATE_BURST_MS, we commit the final target —
      // unless we're already there (leading-edge already landed us
      // on it for a 1-press burst, or a click during the burst
      // moved selection independently).
      if (navBurstTimerRef.current != null) {
        clearTimeout(navBurstTimerRef.current);
      }
      navBurstTimerRef.current = setTimeout(() => {
        navBurstTimerRef.current = null;
        const finalTarget = pendingNavTargetRef.current;
        pendingNavTargetRef.current = null;
        if (finalTarget && finalTarget !== selectedChannelRef.current) {
          fireOpenChannel(finalTarget);
        }
      }, NAVIGATE_BURST_MS);
    },
    [selectedChannel, fireOpenChannel],
  );
  useRegisterCommand("channel.switchPrev", () => navigateChannel(-1));
  useRegisterCommand("channel.switchNext", () => navigateChannel(1));

  const hasPendingSidebarDialog = useMemo(
    () =>
      showCreateWorkspace ||
      !!createChannelContext ||
      showNewDM ||
      showBrowseChannels ||
      showWorkspaceSettings ||
      showEditChannel ||
      showDeleteChannel ||
      !!inviteContext,
    [
      showCreateWorkspace,
      createChannelContext,
      showNewDM,
      showBrowseChannels,
      showWorkspaceSettings,
      showEditChannel,
      showDeleteChannel,
      inviteContext,
    ],
  );

  // If the selected workspace is no longer in the list, fall back to Home
  useSidebarWorkspaceSelectionWithFallback({
    selectedWorkspace,
    workspaceList,
    onGoHome,
  });

  // Detect membership loss: if the user had a membership and it disappears
  // (kicked/removed), show a blocking overlay instead of silently navigating.
  useSidebarMembershipLossDetection({
    selectedWorkspace,
    currentUserWsMembership,
    myWsMembershipsStatus,
    currentWorkspace,
    onMembershipLost,
  });

  // Channel-mutation handlers + memoized ChannelActions for ChannelItem.memo
  const {
    channelActions,
    handleLeaveWorkspace,
    handleDeleteChannel,
    handleToggleArchive,
  } = useChannelActions({
    user,
    zero,
    selectedWorkspace,
    currentWorkspace,
    myMembers,
    channelList,
    selectedChannel,
    onSelectChannel,
    currentUserWsMembership,
    channelById,
    setContextChannel,
    setShowEditChannel,
    setShowDeleteChannel,
    setInviteContext,
    contextChannel,
    onSelectWorkspace,
    getAccessToken,
  });

  // Commit-time probe — fires after this Sidebar render's subtree
  // commits. Difference vs the synchronous `Sidebar.body` log below
  // tells you whether the cost is in this body or in descendants
  // (ChannelItem, DMRowItem, etc.).

  useLayoutEffect(() => {
    csLog(
      "Sidebar.commit",
      csRenderStartRef.current,
      `sel=${selectedChannel?.slice(-6) ?? "-"} ch=${myMembers.length}`,
    );
  });

  csLog(
    "Sidebar.body",
    csRenderStart,
    `sel=${selectedChannel?.slice(-6) ?? "-"} ch=${myMembers.length}`,
  );

  return (
    <nav
      role="navigation"
      aria-label="Workspace sidebar"
      data-sidebar-surface
      className={cn(
        "flex flex-col shrink-0 bg-sidebar relative [-webkit-app-region:no-drag]",
        inDrawer ? "w-full h-full" : "w-60 rounded-bl-[10px]",
      )}
    >
      {!inDrawer && (
        <Separator
          orientation="vertical"
          className="absolute right-0 top-0 bottom-0"
        />
      )}
      {/* Titlebar drag region — Electron-only spacer, not needed inside
          the mobile drawer where the system status bar sits above. */}
      {!inDrawer && (
        <div className="h-[28px] shrink-0 z-20" data-tauri-drag-region />
      )}
      {/* Scope switcher header — grid keeps the switcher bounded so it
          cannot paint over the header actions (#1797). */}
      <div
        ref={setHeaderPortalEl}
        className="relative grid h-12 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 px-2 [-webkit-app-region:no-drag]"
      >
        <Separator className="absolute bottom-0 inset-x-0" />
        <WorkspaceSwitcher
          scope={scope}
          selectedWorkspace={selectedWorkspace}
          onScopeChange={onScopeChange}
          currentWorkspace={currentWorkspace}
          currentUserData={currentUserData}
          userDisplayName={user?.display_name}
          workspaceList={workspaceList}
          workspaceUnreads={workspaceUnreads}
        />
        <div className="relative z-10 flex items-center">
          {!isGuest && user ? (
            <IconTooltip label="New message (DM or group)" side="bottom">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => setShowNewDM(true)}
                aria-label="New message"
                className="mr-1 h-8 w-8 text-foreground/80 hover:text-foreground [-webkit-app-region:no-drag]"
              >
                <ChatText size={18} />
              </Button>
            </IconTooltip>
          ) : null}
          {/* Settings gear (top-right) */}
          {isWorkspaceScope && selectedWorkspace ? (
            <WorkspaceSettingsMenu
              triggerRef={settingsMenuTriggerRef}
              portalContainer={headerPortalEl}
              setShowWorkspaceSettings={setShowWorkspaceSettings}
              showCoworkers={showCoworkers}
              onToggleCoworkers={onToggleCoworkers}
              canManageWorkspace={canAccessTeam}
            />
          ) : null}
        </div>
      </div>

      {!isGuest && <TopSearchBar />}

      {/* Scrollable content area */}
      <div className="flex-1 min-h-0 overflow-y-auto terminal-scrollbar-hidden">
        {/* Inbox nav item — inside the scroll container so it travels
            with the rest of the sidebar items when content overflows. */}
        <div className="px-2 pt-2 pb-0 space-y-0.5">
          <NavItem
            icon={Tray}
            label="Inbox"
            badge={scopedInboxBadgeCount}
            isActive={showInbox}
            onClick={onShowInbox}
          />
          {/* Invites — personal cross-workspace surface, Home scope only
              (peer of the workspace-scoped Team page). */}
          {isHomeScope && onToggleInvites && (
            <NavItem
              icon={EnvelopeSimple}
              label="Invites"
              isActive={!!showInvites}
              onClick={onToggleInvites}
            />
          )}
        </div>

        {/* ─── Home scope content ─── */}
        {isHomeScope && (
          <SidebarHomeContent
            isGuest={isGuest}
            homeWsOpen={homeWsOpen}
            setHomeWsOpen={setHomeWsOpen}
            homeWsFilter={homeWsFilter}
            setHomeWsFilter={setHomeWsFilter}
            setShowCreateWorkspaceHome={setShowCreateWorkspaceHome}
            homeDisplayWorkspaces={homeDisplayWorkspaces}
            workspaceUnreads={workspaceUnreads}
            homeMembershipMap={homeMembershipMap}
            onScopeChange={onScopeChange}
            homeDmsOpen={homeDmsOpen}
            setHomeDmsOpen={setHomeDmsOpen}
            homeDmsFilter={homeDmsFilter}
            setHomeDmsFilter={setHomeDmsFilter}
            setShowNewDM={setShowNewDM}
            user={user ?? null}
            selectedChannel={selectedChannel}
            onSelectChannel={onSelectChannel}
            openChannelWithContextProp={openChannelWithContextProp}
            isUnread={isUnread}
            workspaceList={workspaceList}
          />
        )}

        {/* ─── Workspace scope content ─── */}
        {isWorkspaceScope && (
          <SidebarWorkspaceContent
            isCollaborator={isCollaborator}
            canAccessTeam={canAccessTeam}
            showCoworkers={showCoworkers}
            onToggleCoworkers={onToggleCoworkers}
            showAutomations={showAutomations}
            onToggleAutomations={onToggleAutomations}
            onToggleLists={onToggleLists}
            showLists={showLists}
            selectedListId={selectedListId}
            onSelectList={onSelectList}
            onCreateListItem={onCreateListItem}
            workspaceLists={workspaceLists}
            listsOpen={listsOpen}
            setListsOpen={setListsOpen}
            channelList={channelList}
            displayChannels={displayChannels}
            archivedChannels={archivedChannels}
            channelsOpen={channelsOpen}
            setChannelsOpen={setChannelsOpen}
            channelsFilter={channelsFilter}
            setChannelsFilter={setChannelsFilter}
            setCreateChannelContext={setCreateChannelContext}
            setShowBrowseChannels={setShowBrowseChannels}
            selectedChannel={selectedChannel}
            selectedWorkspace={selectedWorkspace}
            isUnread={isUnread}
            mutedSet={mutedSet}
            myMembers={myMembers}
            getMentionCount={getMentionCount}
            canManageChannel={canManageChannel}
            canDeleteChannel={canDeleteChannel}
            channelsWithCollaborators={channelsWithCollaborators}
            channelActions={channelActions}
            handleToggleArchive={handleToggleArchive}
            onSelectChannel={onSelectChannel}
            isWorkspaceAdmin={isWorkspaceAdmin}
            clientSpaces={clientSpaces}
            displaySpaces={displaySpaces}
            spacesOpen={spacesOpen}
            setSpacesOpen={setSpacesOpen}
            spacesFilter={spacesFilter}
            setSpacesFilter={setSpacesFilter}
            setShowCreateClientSpace={setShowCreateClientSpace}
            expandedSpaces={expandedSpaces}
            toggleSpaceExpanded={toggleSpaceExpanded}
            spaceChannelMap={spaceChannelMap}
            setInviteContext={setInviteContext}
            setSelectedClientSpace={setSelectedClientSpace}
            currentWorkspace={currentWorkspace}
            dmsOpen={dmsOpen}
            setDmsOpen={setDmsOpen}
            dmsFilter={dmsFilter}
            setDmsFilter={setDmsFilter}
            setShowNewDM={setShowNewDM}
            user={user}
          />
        )}
      </div>
      <SidebarUpdateBanner />
      {/* QuickInvite — fixed above the profile footer so it's always one
          click away within a workspace. Invites the person into the active
          workspace, so it only renders in a workspace scope (never Home).
          Workspace invites are admin-only on the server, so it's gated to
          admins/owners; hidden in guest mode (guests can't invite). */}
      {!isGuest &&
        isWorkspaceScope &&
        selectedWorkspace &&
        isWorkspaceAdmin && <QuickInvite workspaceId={selectedWorkspace} />}
      <UserProfileFooter
        workspaceId={selectedWorkspace ?? undefined}
        isGuest={isGuest}
        onSignUp={onSignUp}
      />

      <SidebarDialogs
        user={user}
        selectedWorkspace={selectedWorkspace}
        currentWorkspace={currentWorkspace}
        isWorkspaceAdmin={isWorkspaceAdmin}
        isHomeScope={isHomeScope}
        activeUserList={activeUserList}
        wsMemberIdSet={wsMemberIdSet}
        collaboratorIds={collaboratorIds}
        existingDmUserIds={existingDmUserIds}
        hasPendingSidebarDialog={hasPendingSidebarDialog}
        showCreateWorkspace={showCreateWorkspace}
        setShowCreateWorkspace={setShowCreateWorkspace}
        onSelectWorkspace={onSelectWorkspace}
        onSelectChannel={onSelectChannel}
        setPendingWorkspace={setPendingWorkspace}
        createChannelContext={createChannelContext}
        setCreateChannelContext={setCreateChannelContext}
        setExpandedSpaces={setExpandedSpaces}
        showNewDM={showNewDM}
        setShowNewDM={setShowNewDM}
        showBrowseChannels={showBrowseChannels}
        setShowBrowseChannels={setShowBrowseChannels}
        setSelectedClientSpace={setSelectedClientSpace}
        showWorkspaceSettings={showWorkspaceSettings}
        setShowWorkspaceSettings={setShowWorkspaceSettings}
        handleLeaveWorkspace={handleLeaveWorkspace}
        showCreateClientSpace={showCreateClientSpace}
        setShowCreateClientSpace={setShowCreateClientSpace}
        selectedClientSpace={selectedClientSpace}
        inviteContext={inviteContext}
        setInviteContext={setInviteContext}
        showEditChannel={showEditChannel}
        setShowEditChannel={setShowEditChannel}
        showDeleteChannel={showDeleteChannel}
        setShowDeleteChannel={setShowDeleteChannel}
        contextChannel={contextChannel}
        handleDeleteChannel={handleDeleteChannel}
        showCreateWorkspaceHome={showCreateWorkspaceHome}
        setShowCreateWorkspaceHome={setShowCreateWorkspaceHome}
        bestLookupWorkspaceId={bestLookupWorkspaceId}
        openChannelWithContextProp={openChannelWithContextProp}
        onScopeChange={onScopeChange}
      />
    </nav>
  );
});
